import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Plus, FileText, Receipt, Users, FileSearch, ShoppingCart, Package, Truck, CreditCard, CheckSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { adminAPI, taskAPI } from "@/lib/api";
import { ManualPaymentDialog } from "./ManualPaymentDialog";
import { TaskPanel } from "./TaskPanel";

export default function QuickCreateButton({ user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [workflow, setWorkflow] = useState(null);
  const [showManualPayment, setShowManualPayment] = useState(false);
  const [taskPanelOpen, setTaskPanelOpen] = useState(false);
  const [taskCount, setTaskCount] = useState(0);
  const [taskPrefill, setTaskPrefill] = useState(null);

  useEffect(() => {
    adminAPI.getSettings()
      .then(r => setWorkflow(r.data?.workflow || "devis_facture"))
      .catch(() => setWorkflow("devis_facture"));
  }, [location.pathname]);

  // Listen for external task panel open events
  useEffect(() => {
    const handler = (e) => {
      setTaskPrefill(e.detail || null);
      setTaskPanelOpen(true);
    };
    window.addEventListener("open-task-panel", handler);
    return () => window.removeEventListener("open-task-panel", handler);
  }, []);

  // Load pending task count
  useEffect(() => {
    const loadCount = () => {
      taskAPI.list(false, { background: true }).then(r => {
        setTaskCount((r.data || []).filter(t => !t.done).length);
      }).catch(() => {});
    };
    loadCount();
    const interval = setInterval(loadCount, 60000);
    return () => clearInterval(interval);
  }, []);

  const isDevis = workflow === "devis_facture";
  const isProforma = workflow === "proforma_commande_facture";

  const go = (path) => {
    if (location.pathname === "/documents" && path.startsWith("/documents?")) {
      navigate(path + "&_t=" + Date.now());
    } else {
      navigate(path);
    }
  };

  return (
    <>
    <div className="hidden lg:flex fixed top-5 right-6 z-50 gap-2 items-center">
      {/* Task button */}
      <button
        onClick={() => setTaskPanelOpen(true)}
        className="relative flex items-center justify-center w-10 h-10 rounded-full bg-slate-700 hover:bg-slate-600 text-white shadow-lg transition-all hover:scale-105 active:scale-95"
        title="Taches"
        data-testid="quick-task-btn"
      >
        <CheckSquare size={18} />
        {taskCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1">
            {taskCount > 99 ? "99+" : taskCount}
          </span>
        )}
      </button>
      {/* Nouveau button */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg shadow-indigo-600/25 rounded-full h-10 px-4 gap-1.5 text-sm font-semibold transition-all hover:scale-105 active:scale-95"
            data-testid="quick-create-btn"
          >
            <Plus size={18} strokeWidth={2.5} />
            <span className="hidden sm:inline">Nouveau</span>
          </Button>
        </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52" data-testid="quick-create-menu">
        <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Ventes</DropdownMenuLabel>
        <DropdownMenuGroup>
          {isDevis && (
            <DropdownMenuItem onClick={() => go("/documents?type=devis&new=1")} data-testid="qc-devis-client">
              <FileText size={14} className="mr-2 text-indigo-500" /> Devis client
            </DropdownMenuItem>
          )}
          {isProforma && (
            <>
              <DropdownMenuItem onClick={() => go("/documents?type=proforma&new=1")} data-testid="qc-proforma">
                <FileText size={14} className="mr-2 text-indigo-500" /> Devis
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => go("/documents?type=commande&new=1")} data-testid="qc-commande-client">
                <ShoppingCart size={14} className="mr-2 text-indigo-500" /> Commande client
              </DropdownMenuItem>
            </>
          )}
          <DropdownMenuItem onClick={() => go("/documents?type=facture&new=1")} data-testid="qc-facture">
            <Receipt size={14} className="mr-2 text-emerald-500" /> Facture client
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => go("/clients?new=1")} data-testid="qc-client">
            <Users size={14} className="mr-2 text-sky-500" /> Client
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setShowManualPayment(true)} data-testid="qc-règlement">
            <CreditCard size={14} className="mr-2 text-indigo-500" /> Règlement
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Achats</DropdownMenuLabel>
        <DropdownMenuGroup>
          <DropdownMenuItem onClick={() => go("/fournisseurs?new=devis")} data-testid="qc-devis-fournisseur">
            <FileSearch size={14} className="mr-2 text-amber-500" /> Devis fournisseur
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => go("/fournisseurs?new=commande")} data-testid="qc-commande-fournisseur">
            <ShoppingCart size={14} className="mr-2 text-amber-500" /> Commande fournisseur
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => go("/factures-fournisseurs?new=1&_t=" + Date.now())} data-testid="qc-facture-fournisseur">
            <Receipt size={14} className="mr-2 text-amber-500" /> Facture fournisseur
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => go("/notes-de-frais?new=1")} data-testid="qc-note-de-frais">
            <Receipt size={14} className="mr-2 text-rose-500" /> Note de frais
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Catalogue</DropdownMenuLabel>
        <DropdownMenuGroup>
          <DropdownMenuItem onClick={() => go("/produits?new=1")} data-testid="qc-produit">
            <Package size={14} className="mr-2 text-violet-500" /> Produit
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => go("/fournisseurs?new=supplier")} data-testid="qc-fournisseur">
            <Truck size={14} className="mr-2 text-orange-500" /> Fournisseur
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
    </div>
    <ManualPaymentDialog
      open={showManualPayment}
      onOpenChange={setShowManualPayment}
      onSuccess={() => { navigate(location.pathname + "?_refresh=" + Date.now()); }}
    />
    <TaskPanel
      open={taskPanelOpen}
      onOpenChange={(v) => { setTaskPanelOpen(v); if (!v) { setTaskPrefill(null); taskAPI.list().then(r => setTaskCount((r.data || []).filter(t => !t.done).length)).catch(() => {}); } }}
      isAdmin={user?.is_superadmin || user?.tenant_role === "owner" || user?.tenant_role === "admin"}
      currentUserId={user?.user_id}
      prefill={taskPrefill}
    />
    </>
  );
}
